import { useTranslation } from "react-i18next"
import { type MissedInboundCallReport } from "@repo/core/models"
import { format } from "date-fns"

import { ColumnGroupDef, DashboardTable } from "@/components/DashboardTable"
import { PaginationState } from "@/components/pagination/pagination-state"
import { EventStatusPill } from "@/components/status-pill"
import { EmptyValue, TableLink } from "@/components/table-helpers"
import { TruncatedTableCell } from "@/components/TruncatedParagraph"
import { CopyIdButton } from "@/components/ui/copy-id-button"
import { formatFullName } from "@/helpers/names"
import { useTableSorting } from "@/hooks/table-sorting"

type TableProps = {
  isLoading: boolean
  missedCalls: MissedInboundCallReport[]
  sortState: ReturnType<typeof useTableSorting>
} & PaginationState

function MissedCallsTable({
  missedCalls,
  sortState,
  ...restProps
}: TableProps) {
  const columns = useColumns()
  const { sortOptions, toggleSort } = sortState

  return (
    <DashboardTable
      columnGroups={columns}
      items={missedCalls}
      getItemId={(missedCall) => missedCall._id}
      sortOptions={sortOptions}
      toggleSort={toggleSort}
      {...restProps}
    />
  )
}

function useColumns() {
  const { t } = useTranslation()

  const columns: ColumnGroupDef<MissedInboundCallReport>[] = [
    {
      header: t("missedCalls.cellHeading.name"),
      cols: [
        {
          id: "name",
          cell: (missedCall) => {
            return (
              <TableLink
                to={`/campaigns/${missedCall.campaign_id}/students/${missedCall.entor_id}`}
              >
                <div className="font-body-medium-bold">
                  {formatFullName(
                    missedCall.first_name || null,
                    missedCall.last_name || null,
                  )}
                </div>
              </TableLink>
            )
          },
        },
      ],
    },
    {
      header: t("missedCalls.cellHeading.entorId"),
      cols: [
        {
          id: "student_id",
          className: "min-w-32",
          cell: (missedCall) => (
            <CopyIdButton className="w-32" uuid={missedCall.entor_id} />
          ),
        },
      ],
    },
    {
      header: t("missedCalls.cellHeading.legacyId"),
      cols: [
        {
          id: "legacy_id",
          className: "min-w-32",
          cell: (missedCall) => (
            <CopyIdButton
              className="w-full"
              uuid={missedCall.legacy_id.toString()}
            />
          ),
        },
      ],
    },
    {
      header: t("missedCalls.cellHeading.status"),
      cols: [
        {
          id: "status",
          cell: (missedCall) => <EventStatusPill value={missedCall.status} />,
        },
      ],
    },
    {
      header: t("missedCalls.cellHeading.updatedAt"),
      sortKey: "created_at",
      cols: [
        {
          id: "created_at",
          cell: (missedCall) => {
            const date = missedCall.created_at

            return date ? (
              <>{format(date, "yyyy/MM/dd HH:mm")}</>
            ) : (
              <EmptyValue />
            )
          },
        },
      ],
    },
    {
      header: t("missedCalls.cellHeading.campaign"),
      cols: [
        {
          id: "campaign_id",
          cell: (missedCall) => <>{missedCall.campaign_id}</>,
        },
      ],
    },
    {
      header: t("missedCalls.cellHeading.memo"),
      cols: [
        {
          id: "memo",
          cell: (missedCall) => {
            const memo = missedCall.call_comment

            return memo ? <TruncatedTableCell text={memo} /> : <EmptyValue />
          },
        },
      ],
    },
  ]

  return columns
}

export default MissedCallsTable
